import { useMemo } from 'react';
import ActionButton from '@/components/Button/ActionButton';
import IconButton from '@/components/Button/IconButton';
import { InputPhoneNumber } from '@/components/Input/Phone/InputPhoneNumber';
import type { PhoneValue } from '@/components/Input/Phone/state.machine';
import { HStack, VStack } from '@/components/Layout';
import { ControlledSelect } from '@/components/Select/ControlledSelect';
import { Text } from '@/components/Typography/Text';
import type { PhoneListItemValue, PhoneType } from '@/features/section-attribute/types';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { useFormattedPhoneNumber } from '@/hooks/useFormattedPhoneNumber';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { CFormItemBase, type CFormItemProps, buildCFormItem } from '../CFormItem';
import { JAPAN_DIAL_CODE, PHONE_TYPES } from './CFormPhoneItem';
type Props = CFormItemProps & {
  value: PhoneListItemValue[];
  onChange: (value: PhoneListItemValue[]) => void;
  addButtonLabel?: string;
  showPhoneType?: boolean;
};
function FormattedPhone({
  phone
}: {
  phone: PhoneListItemValue;
}) {
  const {
    formattedFull
  } = useFormattedPhoneNumber(phone);
  return <Text variant="labelLarge">{formattedFull}</Text>;
}
export const CFormPhoneListItem = buildCFormItem((props: Props) => {
  const tc = useCommonTranslation();
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'features.cForm.phoneItem'
  });
  const values = props.value ?? [];
  const phoneTypeOpts = useMemo(() => PHONE_TYPES.map(v => ({
    value: v,
    label: String(t(`phoneType.${v}`))
  })), [t]);
  const handlePhoneChange = (index: number, phone: PhoneValue) => {
    const next = [...values];
    next[index] = {
      ...next[index],
      ...phone
    };
    props.onChange(next);
  };
  const handlePhoneTypeChange = (index: number, phoneType: string) => {
    const next = [...values];
    next[index] = {
      ...next[index],
      phoneType: phoneType as PhoneType || undefined
    };
    props.onChange(next);
  };
  const handleAdd = () => {
    props.onChange([...values, {
      dialCode: JAPAN_DIAL_CODE,
      phoneNumber: '',
      phoneType: undefined
    }]);
  };
  const handleRemove = (index: number) => {
    props.onChange(values.filter((_, i) => i !== index));
  };
  const validValues = values.filter(v => v.phoneNumber.trim() !== '');
  const readComponent = <VStack className="gap-2">
      {validValues.length === 0 ? <Text variant="pSmall" className="text-neutral-muted-on-fill">
          -
        </Text> : validValues.map((v, i) => {
      const label = v.phoneType ? phoneTypeOpts.find(o => o.value === v.phoneType)?.label : undefined;
      return <VStack key={`phone-read-${i}`} className="gap-0.5">
              {label && <Text variant="pSmall" className="text-neutral-muted-on-fill">
                  {label}
                </Text>}
              <FormattedPhone phone={v} />
            </VStack>;
    })}
    </VStack>;
  const writeComponent = <VStack className="gap-5">
      {values.map((v, index) => <VStack key={`phone-write-${index}`} className={props.showPhoneType ? 'gap-2 border-l-1 border-neutral-muted-border pl-4 py-2' : 'gap-2'}>
          <HStack className="gap-2 items-end">
            <VStack className="flex-1 gap-2">
              {props.showPhoneType && <VStack className="gap-1">
                  <Text variant="label" className="text-neutral-muted-on-fill">
                    {t('phoneType.label')}
                  </Text>
                  <ControlledSelect id={`${props.id}-${index}-phoneType`} value={v.phoneType ?? ''} label={phoneTypeOpts.find(o => o.value === v.phoneType)?.label} onChange={option => handlePhoneTypeChange(index, option?.value ?? '')} fetchOptions={async () => phoneTypeOpts} allowEmpty disabled={props.readonly} hideSearch width="max-w-56" />
                </VStack>}
              <VStack className="gap-1">
                {props.showPhoneType && <Text variant="label" className="text-neutral-muted-on-fill">
                    {props.label}
                  </Text>}
                <InputPhoneNumber value={v} onChange={phone => handlePhoneChange(index, phone)} required={props.required} />
              </VStack>
            </VStack>
            {!props.readonly && <IconButton icon="Trash2Icon" variant="ghost" size="Small" className="shrink-0 mb-1" onClick={() => handleRemove(index)} aria-label={tc('action.delete')} />}
          </HStack>
        </VStack>)}
      {!props.readonly && <ActionButton icon="PlusIcon" variant="ghost" size="sm" className="self-start" onClick={handleAdd}>
          {props.addButtonLabel ?? tc('action.add')}
        </ActionButton>}
    </VStack>;
  return <CFormItemBase {...props} readComponent={readComponent} writeComponent={writeComponent} />;
});